import React from 'react';
import { cn } from '@/lib/utils';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PagePathInputProps {
  /** The domain to display as prefix (e.g. "www.example.org" or "https://example.org"). Protocol is stripped for display. */
  domain: string;
  /** Current path value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Size variant: 'default' for drawer forms, 'sm' for compact/snippet contexts */
  size?: 'default' | 'sm';
  /** Override placeholder text */
  placeholder?: string;
  /** Additional className for the outer wrapper */
  className?: string;
}

/**
 * Reusable page-path input with domain prefix, label, and tooltip.
 * Used in URLBuilderDrawer and OverlaySnippets for overlay page-path selection.
 */
export function PagePathInput({
  domain,
  value,
  onChange,
  size = 'default',
  placeholder = 'optional-custom-path',
  className,
}: PagePathInputProps) {
  const domainDisplay = domain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  const isSmall = size === 'sm';

  return (
    <div className={cn(isSmall && 'mb-2', className)}>
      <div className="flex items-center gap-1 mb-1">
        {isSmall ? (
          <span className="text-[11px] font-medium text-ink-muted">Page Path</span>
        ) : (
          <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
            Page Path
          </Label>
        )}
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <InformationCircleIcon className="h-3.5 w-3.5 cursor-help text-ink-muted hover:text-ink-secondary" />
            </TooltipTrigger>
            <TooltipContent
              side="top"
              {...(isSmall ? { align: 'start' } : {})}
              className="max-w-xs text-left"
            >
              <p className="text-xs leading-relaxed">
                <span className="font-semibold">Page Path</span> — Choose which
                page on your website opens with the overlay on top.
              </p>
              <ul className="mt-1.5 ml-3 list-disc text-xs leading-relaxed text-ink-muted">
                <li>The page must exist on your site</li>
                <li>The CharityStack embed script must be installed on that page</li>
              </ul>
              <p className="mt-1.5 text-xs text-ink-muted">
                Leave blank to use your homepage. Find the embed script in
                Settings &rarr; Embed.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div
        className={cn(
          'flex items-center rounded-md border font-mono overflow-hidden',
          isSmall
            ? 'border-edge bg-paper-inset text-[11px] h-[30px]'
            : 'border-edge-input bg-white text-sm h-7',
        )}
      >
        <span
          className={cn(
            'shrink-0 text-ink border-r whitespace-nowrap',
            isSmall
              ? 'px-2 border-edge leading-[30px]'
              : 'px-2.5 border-edge-input leading-none',
          )}
        >
          {domainDisplay}/
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={isSmall ? { backgroundColor: 'var(--paper-muted)' } : undefined}
          className={cn(
            'flex-1 min-w-0 px-2 text-ink placeholder:text-ink-muted/30 placeholder:italic outline-none border-0 ring-0 focus:outline-none focus:ring-0 h-full',
            isSmall
              ? 'text-[11px]'
              : 'rounded-r-md bg-white text-sm focus:border-0',
          )}
        />
      </div>
    </div>
  );
}
